import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FormDialog } from '@/components/shared/form-dialog';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { AttendanceSubnav } from '@/pages/attendance/attendance-subnav';

interface Kiosk {
  id: string;
  name: string;
  pairingCode: string;
  pairingUrl: string;
  status: 'unpaired' | 'paired' | 'revoked';
  currentSession: {
    id: string;
    status: 'open' | 'closed';
    gatheringName: string;
  } | null;
}

export const KiosksPage = () => {
  const { t } = useTranslation('attendance');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<Kiosk | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<Kiosk | null>(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimCode, setClaimCode] = useState('');
  const [renameTarget, setRenameTarget] = useState<Kiosk | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const claim = useMutation({
    mutationFn: () => api.post('/attendance/kiosks/claim', { pairingCode: claimCode.trim() }),
    onSuccess: () => {
      toast.success(t('kiosks.claimSuccess'));
      setClaimOpen(false);
      setClaimCode('');
      void queryClient.invalidateQueries({ queryKey: ['attendance-kiosks'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const list = useQuery({
    queryKey: ['attendance-kiosks'],
    queryFn: () => api.get<{ data: Kiosk[] }>('/attendance/kiosks'),
    refetchInterval: 15_000,
  });

  const create = useMutation({
    mutationFn: () => api.post<Kiosk>('/attendance/kiosks', { name }),
    onSuccess: () => {
      toast.success(t('kiosks.saved'));
      setOpen(false);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['attendance-kiosks'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const revoke = useMutation({
    mutationFn: (kioskId: string) => api.post<Kiosk>(`/attendance/kiosks/${kioskId}/revoke`),
    onSuccess: () => {
      toast.success(t('kiosks.revoked'));
      setRevokeTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['attendance-kiosks'] });
      void queryClient.invalidateQueries({ queryKey: ['attendance-sessions'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const disconnect = useMutation({
    mutationFn: (kioskId: string) =>
      api.post<Kiosk>(`/attendance/kiosks/${kioskId}/disconnect`),
    onSuccess: () => {
      toast.success(t('kiosks.disconnected'));
      setDisconnectTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['attendance-kiosks'] });
      void queryClient.invalidateQueries({ queryKey: ['attendance-sessions'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const openKiosk = useMutation({
    mutationFn: (kioskId: string) =>
      api.post<{ url: string }>(`/attendance/kiosks/${kioskId}/display`),
    onSuccess: (result) => {
      window.open(result.url, '_blank', 'noopener,noreferrer');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<Kiosk>(`/attendance/kiosks/${id}`, { name }),
    onSuccess: () => {
      toast.success(t('kiosks.renamed'));
      setRenameTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['attendance-kiosks'] });
      void queryClient.invalidateQueries({ queryKey: ['attendance-sessions'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const renameReady = renameValue.trim().length > 0;

  const statusLabel = (status: Kiosk['status']) => {
    if (status === 'paired') return t('kiosks.statusPaired');
    if (status === 'revoked') return t('kiosks.statusRevoked');
    return t('kiosks.statusUnpaired');
  };

  const columns: DataTableColumn<Kiosk>[] = [
    {
      id: 'name',
      header: t('kiosks.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'status',
      header: t('kiosks.columns.status'),
      cell: (row) => (
        <Badge variant={row.status === 'paired' ? 'success' : 'neutral'}>
          {statusLabel(row.status)}
        </Badge>
      ),
    },
    {
      id: 'session',
      header: t('kiosks.columns.session'),
      cell: (row) =>
        row.currentSession ? (
          <Link
            to={`/attendance/sessions/${row.currentSession.id}`}
            className="text-body-sm font-medium text-brand hover:underline"
          >
            {row.currentSession.gatheringName}
          </Link>
        ) : (
          <span className="text-body-sm text-fg-secondary">{t('kiosks.notConnected')}</span>
        ),
    },
    {
      id: 'pairingCode',
      header: t('kiosks.columns.pairingCode'),
      cell: (row) => row.pairingCode,
    },
    {
      id: 'pairingUrl',
      header: t('kiosks.columns.pairingUrl'),
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="text-body-sm text-brand hover:underline"
            onClick={() => openKiosk.mutate(row.id)}
          >
            {t('kiosks.openKiosk')}
          </button>
          <button
            type="button"
            className="text-body-sm text-brand hover:underline"
            onClick={() => {
              void navigator.clipboard.writeText(row.pairingUrl).then(() => {
                toast.success(tCommon('toast.copiedToClipboard'));
              });
            }}
          >
            {t('kiosks.copyLink')}
          </button>
        </div>
      ),
    },
  ];

  const rows = list.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title={t('kiosks.title')}
        description={t('kiosks.description')}
        tipId="attendance.kiosk.purpose"
        headerActions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setClaimOpen(true)}>
              {t('kiosks.claim')}
            </Button>
            <Button onClick={() => setOpen(true)}>{t('kiosks.create')}</Button>
          </div>
        }
      />
      <AttendanceSubnav />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('kiosks.empty')}</p>}
        rowActions={(row) =>
          row.status !== 'revoked' ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setRenameTarget(row);
                  setRenameValue(row.name);
                }}
              >
                {t('kiosks.rename')}
              </Button>
              {row.currentSession ? (
                <Button variant="outline" size="sm" onClick={() => setDisconnectTarget(row)}>
                  {t('kiosks.disconnect')}
                </Button>
              ) : null}
              <Button variant="destructive" size="sm" onClick={() => setRevokeTarget(row)}>
                {t('kiosks.revoke')}
              </Button>
            </div>
          ) : null
        }
        actionsHeader={tCommon('table.actions')}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('kiosks.create')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="kioskName">{t('kiosks.name')}</Label>
              <Input id="kioskName" value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
                {t('kiosks.create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('kiosks.claim')}</DialogTitle>
          </DialogHeader>
          <p className="text-body-sm text-fg-secondary">{t('kiosks.claimDescription')}</p>
          <div className="space-y-3">
            <div>
              <Label htmlFor="claimCode">{t('kiosks.claimCode')}</Label>
              <Input
                id="claimCode"
                inputMode="numeric"
                value={claimCode}
                onChange={(event) => setClaimCode(event.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setClaimOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                disabled={claimCode.trim().length < 4 || claim.isPending}
                onClick={() => claim.mutate()}
              >
                {t('kiosks.claim')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <FormDialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null);
        }}
        title={t('kiosks.renameTitle')}
        size="md"
        submitLabel={t('kiosks.renameConfirm')}
        cancelLabel={tCommon('action.cancel')}
        closeLabel={tCommon('action.close')}
        submitting={rename.isPending}
        submitDisabled={!renameReady}
        onSubmit={() => {
          if (!renameTarget || !renameReady) return;
          rename.mutate({ id: renameTarget.id, name: renameValue });
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="kioskRename">{t('kiosks.name')}</Label>
          <Input
            id="kioskRename"
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            maxLength={120}
            autoFocus
          />
        </div>
      </FormDialog>

      <ConfirmDialog
        open={disconnectTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDisconnectTarget(null);
        }}
        title={t('confirm.disconnectFromSession.title', { name: disconnectTarget?.name ?? '' })}
        description={t('confirm.disconnectFromSession.description', {
          session: disconnectTarget?.currentSession?.gatheringName ?? '',
        })}
        confirmLabel={t('confirm.disconnectFromSession.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={disconnect.isPending}
        onConfirm={() => {
          if (disconnectTarget) disconnect.mutate(disconnectTarget.id);
        }}
      />

      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRevokeTarget(null);
        }}
        title={t('confirm.revokeKiosk.title', { name: revokeTarget?.name ?? '' })}
        description={t('confirm.revokeKiosk.description')}
        confirmLabel={t('confirm.revokeKiosk.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={revoke.isPending}
        onConfirm={() => {
          if (revokeTarget) revoke.mutate(revokeTarget.id);
        }}
      />
    </div>
  );
};
